<div class="flex flex-col h-full overflow-hidden">
  <div class="flex-1 min-h-0 overflow-y-auto">
    @if (phase() === 'step') {
      @if (scenariosLoading()) {
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 p-3">
          @for (placeholder of [1, 2, 3]; track placeholder) {
            <div class="w-full rounded-lg border border-surface-200 overflow-hidden">
              <div class="w-full aspect-video">
                <p-skeleton width="100%" height="100%" styleClass="!rounded-none" />
              </div>
              <div class="flex flex-col gap-2 px-4 py-3.5">
                <p-skeleton width="12rem" height="1rem" />
                <p-skeleton width="5rem" height="0.75rem" />
              </div>
            </div>
          }
        </div>
      } @else if (scenariosHasError()) {
        <div class="flex flex-col items-center justify-center h-full gap-3 p-8 text-center">
          <i class="pi pi-times-circle text-red-600" style="font-size: 2rem"></i>
          <p class="text-body-sm-500 text-surface-700 m-0">Could not load the guided steps.</p>
          <p class="text-body-sm text-surface-500 m-0">Something went wrong while loading them for this deal.</p>
          <p-button label="Retry" icon="pi pi-refresh" size="small" [outlined]="true" (onClick)="retryScenarios()" />
        </div>
      } @else if (!steps().length) {
        <div class="flex flex-col items-center justify-center h-full gap-3 text-surface-400 p-8 text-center">
          <i class="bi bi-diagram-3" style="font-size: 3rem"></i>
          <p class="text-body-sm m-0">There are no guided steps set up for this home.</p>
        </div>
      } @else {
        <div class="px-3 pt-1">
          <h3 class="text-body-lg-600 text-surface-700 m-0">Choose where to start</h3>
          <p class="text-body-sm text-surface-500 m-0 mt-1.5">
            These are the areas set up for this home. Open one to see the options available in it — you can come back
            and pick another at any time.
          </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 p-3">
          @for (step of steps(); track step.id; let index = $index) {
            <div
              class="group w-full rounded-lg border border-surface-200 bg-white overflow-hidden cursor-pointer transition-all duration-200 hover:border-primary-500 hover:shadow-module"
              (click)="selectStep(step.id)"
            >
              <div class="relative w-full aspect-video bg-surface-100 overflow-hidden">
                <img
                  [src]="stepImageUrl(step)"
                  [alt]="step.name"
                  class="w-full h-full transition-transform duration-300"
                  [class]="hasStepImage(step) ? 'object-cover group-hover:scale-[1.03]' : 'object-contain p-4'"
                />
                <span
                  class="absolute top-3 left-3 inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-500 text-white text-body-xs-700 leading-none shadow-module"
                  aria-hidden="true"
                >
                  {{ index + 1 }}
                </span>
              </div>
              <div class="flex items-center justify-between gap-4 px-4 py-3.5">
                <div class="flex flex-col min-w-0 gap-0.5">
                  @if (activeScenario()?.name) {
                    <span class="text-body-2xs-600 text-surface-400 uppercase tracking-wider truncate">
                      {{ activeScenario()?.name }}
                    </span>
                  }
                  <span class="text-body-md-600 text-surface-700 truncate">
                    <span class="sr-only">Step {{ index + 1 }}: </span>{{ step.name }}
                  </span>
                </div>
                <div class="flex items-center gap-3 shrink-0">
                  <!-- <span class="text-body-sm text-surface-500 whitespace-nowrap">{{ stepOptionCountLabel(step) }}</span> -->
                  <i
                    class="pi pi-chevron-right text-surface-400 transition-colors duration-200 group-hover:text-primary-500"
                  ></i>
                </div>
              </div>
            </div>
          }
        </div>
      }
    } @else {
      <div class="flex flex-col h-full min-h-0">
        <div class="flex items-center gap-3 shrink-0 px-3 pb-1">
          <button
            type="button"
            class="flex shrink-0 items-center justify-center w-8 h-8 bg-transparent border-0 text-primary-500 rounded-full outline outline-1 outline-primary-200 hover:bg-primary-50 transition-colors cursor-pointer"
            title="Back"
            (click)="backToSteps()"
          >
            <i class="pi pi-arrow-left text-sm"></i>
          </button>
          <div class="flex flex-col min-w-0">
            <span class="text-body-2xs-600 text-surface-400 uppercase tracking-wider">
              Step {{ selectedStepNumber() }} of {{ steps().length }}
            </span>
            <span class="text-body-lg-600 text-surface-700 truncate">{{ selectedStep()?.name }}</span>
          </div>
        </div>

        @if (stepScopeLoading()) {
          <div class="flex flex-col gap-3 p-3">
            <p-skeleton width="100%" height="2.5rem" />
            <p-skeleton width="60%" height="0.75rem" />
          </div>
        } @else if (stepScopeHasError()) {
          <div class="flex flex-col items-center justify-center flex-1 gap-3 p-8 text-center">
            <i class="pi pi-times-circle text-red-600" style="font-size: 2rem"></i>
            <p class="text-body-sm-500 text-surface-700 m-0">Could not load this step.</p>
            <p class="text-body-sm text-surface-500 m-0">
              Something went wrong while loading the categories this step covers.
            </p>
            <p-button label="Retry" icon="pi pi-refresh" size="small" [outlined]="true" (onClick)="retryStepScope()" />
          </div>
        } @else if (!stepHasScope()) {
          <div class="flex flex-col items-center justify-center flex-1 gap-3 text-surface-400 p-8 text-center">
            <i class="bi bi-info-circle" style="font-size: 3rem"></i>
            <p class="text-body-sm m-0">This step does not cover any categories yet.</p>
          </div>
        } @else {
          <dsm-add-option-options-panel
            class="flex flex-col flex-1 min-h-0 gap-5 pt-3"
            [modelId]="modelId()"
            [elevationId]="elevationId()"
            [lotId]="lotId()"
            [addedProductIds]="addedProductIds()"
            [selectedEntries]="selectedEntries()"
            [sidebarPinned]="sidebarPinned()"
            [categoryOptionsOverride]="stepCategoryOptions()"
            [subcategoryOptionsOverride]="stepSubcategoryOptions()"
            [(filtersVisible)]="filtersVisible"
            (productPicked)="productPicked.emit($event)"
          />
        }
      </div>
    }
  </div>
</div>
